import { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ComponentLoader from '@shared/components/ComponentLoader/ComponentLoader';
import ComponentValueChecker from '@shared/components/ComponentValueChecker/ComponentValueChecker';
import type { OrganisationTheme } from '@shared/models/organisation';
import type { CustomerCreditDashboardResponse } from '@api/customerCreditDashboard';
import styles from './CustomerCreditDashboard.module.scss';
import { useTranslation } from 'react-i18next';
import '../../translations/translation';

export interface CustomerCreditDashboardProps {
  data?: CustomerCreditDashboardResponse;
  isError: boolean;
  isLoading: boolean;
  theme?: OrganisationTheme | null;
}

const CustomerCreditDashboard = ({ data, isError, isLoading, theme }: CustomerCreditDashboardProps) => {
  const { t } = useTranslation();
  const chartData = useMemo(
    () => data?.consumption.map(({ bucket, chargedCredits, questionCount }) => ({ bucket, chargedCredits, questionCount })) ?? [],
    [data],
  );
  const creditKpis = [
    { label: t('creditDashboard.allowance'), tone: 'allowance', value: data?.allowanceCredits },
    { label: t('creditDashboard.consumed'), tone: 'consumed', value: data?.consumedCredits },
    { label: t('creditDashboard.remaining'), tone: 'remaining', value: data?.remainingCredits },
  ];

  return (
    <section className={styles.section} aria-labelledby="credit-dashboard-heading">
      <h2 id="credit-dashboard-heading" className={styles.visuallyHidden}>{t('creditDashboard.heading')}</h2>
      <div className={styles.kpis}>
        {creditKpis.map(({ label, tone, value }) => (
          <div className={styles.kpi} data-credit-tone={tone} key={label}>
            <ComponentLoader isLoading={isLoading} lines={2}>
              <ComponentValueChecker value={value} isError={isError}>
                <div className={styles.value}>{value}</div>
              </ComponentValueChecker>
            </ComponentLoader>
            <div className={styles.label}>{label}</div>
          </div>
        ))}
      </div>
      <div className={styles.chart} data-y-axes="shared">
        <h2 className={styles.chartTitle}>{t('creditDashboard.consumption')}</h2>
        <div
          aria-label={t('creditDashboard.consumption')}
          className={styles.seriesLabels}
          data-testid="customer-credit-series-labels"
        >
          <div className={styles.seriesLabel}>
            <span
              aria-hidden="true"
              className={styles.seriesSwatch}
              style={{ backgroundColor: styles.questionsSeriesColor }}
            />
            {t('creditDashboard.questionsSeries')}
          </div>
          <div className={styles.seriesLabel}>
            <span
              aria-hidden="true"
              className={styles.seriesSwatch}
              style={{ backgroundColor: styles.creditsSeriesColor }}
            />
            {t('creditDashboard.creditsSeries')}
          </div>
        </div>
        <ul className={styles.visuallyHidden} data-point-count={chartData.length} data-testid="customer-credit-series-points">
          {chartData.map((point) => (
            <li key={point.bucket}>{`${point.bucket}: ${point.questionCount} ${t('creditDashboard.questionsSeries')}; ${point.chargedCredits} ${t('creditDashboard.creditsSeries')}`}</li>
          ))}
        </ul>
        <div className={styles.chartContent}>
          <ComponentLoader isLoading={isLoading} lines={5}>
            <ComponentValueChecker value={chartData} isError={isError}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" stroke={theme?.text_color} tick={{ fill: theme?.text_color }} />
                  <YAxis
                    allowDecimals={false}
                    stroke={theme?.text_color}
                    tick={{ fill: theme?.text_color }}
                  />
                  <Tooltip labelStyle={{ color: theme?.text_color }} />
                  <Line
                    type="monotone"
                    dataKey="questionCount"
                    name={t('creditDashboard.questionsSeries')}
                    stroke={styles.questionsSeriesColor}
                    strokeWidth={2}
                    dot
                  />
                  <Line
                    type="monotone"
                    dataKey="chargedCredits"
                    name={t('creditDashboard.creditsSeries')}
                    stroke={styles.creditsSeriesColor}
                    strokeWidth={2}
                    dot
                  />
                </LineChart>
              </ResponsiveContainer>
            </ComponentValueChecker>
          </ComponentLoader>
        </div>
      </div>
    </section>
  );
};

export default CustomerCreditDashboard;
